import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Save,
  Loader2,
  AlertTriangle,
  ImagePlus,
  ExternalLink,
  Trash2,
  CheckCircle2,
  CircleDot,
  CircleSlash,
} from "lucide-react";
import { API } from "@/admin/AuthContext";

const SPECIALTY_SUGGESTIONS = [
  "Mandatos majoritários",
  "Proporcionais",
  "Comunicação de governo",
  "Mobilização de base",
  "Mídia política",
  "Pesquisa regional",
  "Estratégia digital",
  "Coordenação de QG",
];

/**
 * Reusable partner editor. role = 'partner' (self) | 'admin'
 * partnerSlug is required. asAdmin shows the locked-down extra fields.
 */
export default function PartnerEditor({ partnerSlug, asAdmin = false, onSaved }) {
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [specialtyInput, setSpecialtyInput] = useState("");
  const fileInput = useRef(null);
  const specialties = partner?.specialties;
  const availableSuggestions = useMemo(
    () => SPECIALTY_SUGGESTIONS.filter((s) => !(specialties || []).includes(s)),
    [specialties],
  );

  useEffect(() => {
    if (!partnerSlug) return;
    let cancel = false;
    setLoading(true);
    axios
      .get(`${API}/partners/${partnerSlug}`)
      .then(({ data }) => {
        if (!cancel) setPartner(data);
      })
      .catch(() => {
        if (!cancel) setError("Não foi possível carregar o perfil.");
      })
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, [partnerSlug]);

  const set = (k) => (e) => setPartner({ ...partner, [k]: e.target.value });

  const addSpecialty = (val) => {
    const v = (val ?? specialtyInput).trim();
    if (!v) return;
    const next = Array.from(new Set([...(partner.specialties || []), v]));
    setPartner({ ...partner, specialties: next });
    setSpecialtyInput("");
  };
  const removeSpecialty = (v) => {
    setPartner({
      ...partner,
      specialties: (partner.specialties || []).filter((s) => s !== v),
    });
  };

  const save = async (e) => {
    e?.preventDefault();
    if (saving) return;
    setSaving(true);
    const body = {
      tagline: partner.tagline || null,
      description: partner.description || null,
      city: partner.city || null,
      state: partner.state || null,
      website: partner.website || null,
      contact_email: partner.contact_email || null,
      logo_url: partner.logo_url || null,
      specialties: partner.specialties || [],
      availability: partner.availability || "accepting",
    };
    if (asAdmin) {
      body.name = partner.name;
      body.type = partner.type;
      body.tier = partner.tier;
      body.featured = !!partner.featured;
      body.is_placeholder = !!partner.is_placeholder;
    }
    try {
      const { data } = await axios.patch(`${API}/partners/${partnerSlug}`, body, {
        withCredentials: true,
      });
      setPartner(data);
      toast.success("Perfil atualizado.");
      onSaved?.(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error("Imagem maior que 1 MB. Reduza antes de enviar.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await axios.post(
        `${API}/uploads/logo?slug=${encodeURIComponent(partnerSlug)}`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } },
      );
      setPartner({ ...partner, logo_url: data.url });
      toast.success("Logo atualizado.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Falha no upload.");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-[#7D8188]">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }
  if (error || !partner) {
    return (
      <div className="rounded-2xl border border-[#D94A0A]/30 bg-white p-6 text-[#D94A0A]">
        <AlertTriangle size={18} className="inline-block mr-2" />
        {error || "Perfil não encontrado."}
      </div>
    );
  }

  return (
    <form
      data-testid="partner-editor"
      onSubmit={save}
      className="grid grid-cols-1 lg:grid-cols-12 gap-6"
    >
      <div className="lg:col-span-8 space-y-4">
        <Card>
          <h3 className="font-display text-base font-semibold text-[#1B1D1D]">
            Identidade pública
          </h3>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {asAdmin && (
              <>
                <Field label="Nome">
                  <input
                    data-testid="editor-input-name"
                    value={partner.name || ""}
                    onChange={set("name")}
                    className={inputCls}
                  />
                </Field>
                <Field label="Tipo">
                  <select
                    data-testid="editor-input-type"
                    value={partner.type || ""}
                    onChange={set("type")}
                    className={inputCls}
                  >
                    <option value="agencia">Agência</option>
                    <option value="consultor">Consultor</option>
                  </select>
                </Field>
              </>
            )}
            <Field label="Tagline (linha curta no card)" className="sm:col-span-2">
              <input
                data-testid="editor-input-tagline"
                value={partner.tagline || ""}
                onChange={set("tagline")}
                placeholder="Uma frase que descreve o seu trabalho"
                maxLength={180}
                className={inputCls}
              />
            </Field>
            <Field label="Descrição completa" className="sm:col-span-2">
              <textarea
                data-testid="editor-input-description"
                value={partner.description || ""}
                onChange={set("description")}
                rows={5}
                placeholder="Conte sobre a sua atuação, perfil de clientes, diferenciais…"
                maxLength={2000}
                className={`${inputCls} resize-none`}
              />
            </Field>
          </div>
        </Card>

        <Card>
          <h3 className="font-display text-base font-semibold text-[#1B1D1D]">
            Localização e contato
          </h3>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Cidade">
              <input
                data-testid="editor-input-city"
                value={partner.city || ""}
                onChange={set("city")}
                className={inputCls}
              />
            </Field>
            <Field label="Estado (UF)">
              <input
                data-testid="editor-input-state"
                value={partner.state || ""}
                onChange={set("state")}
                maxLength={4}
                className={inputCls}
              />
            </Field>
            <Field label="Site" className="sm:col-span-2">
              <input
                data-testid="editor-input-website"
                value={partner.website || ""}
                onChange={set("website")}
                placeholder="https://"
                className={inputCls}
              />
            </Field>
            <Field label="E-mail de contato" className="sm:col-span-2">
              <input
                data-testid="editor-input-email"
                value={partner.contact_email || ""}
                onChange={set("contact_email")}
                placeholder="contato@empresa.com"
                className={inputCls}
              />
              <p className="mt-1.5 text-[11px] text-[#7D8188]">
                Atenção: o e-mail aqui também é a sua chave de acesso ao painel.
              </p>
            </Field>
          </div>
        </Card>

        <Card>
          <h3 className="font-display text-base font-semibold text-[#1B1D1D]">
            Especialidades
          </h3>
          <p className="mt-1 text-xs text-[#7D8188]">
            Aparecem como filtros e tags no perfil.
          </p>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="editor-specialties-list">
            {(partner.specialties || []).map((s) => (
              <span
                key={s}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#F5FAFB] px-3 py-1 text-xs font-medium text-[#1B1D1D] border border-[#1B1D1D]/8"
              >
                {s}
                <button
                  type="button"
                  onClick={() => removeSpecialty(s)}
                  data-testid={`editor-specialty-remove-${s}`}
                  className="text-[#7D8188] hover:text-[#D94A0A]"
                >
                  <Trash2 size={12} />
                </button>
              </span>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <input
              data-testid="editor-input-specialty"
              value={specialtyInput}
              onChange={(e) => setSpecialtyInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addSpecialty();
                }
              }}
              placeholder="Adicionar especialidade…"
              className={inputCls}
            />
            <button
              type="button"
              data-testid="editor-specialty-add"
              onClick={() => addSpecialty()}
              className="rounded-xl bg-[#1B1D1D] px-4 text-sm font-medium text-white hover:bg-[#7E20FF] transition-colors"
            >
              Adicionar
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {availableSuggestions.map((s) => (
              <button
                type="button"
                key={s}
                data-testid={`editor-specialty-suggestion-${s}`}
                onClick={() => addSpecialty(s)}
                className="text-[11px] tracking-wide rounded-full border border-dashed border-[#1B1D1D]/15 px-2.5 py-1 text-[#51565F] hover:border-[#7E20FF] hover:text-[#7E20FF] transition-colors"
              >
                + {s}
              </button>
            ))}
          </div>
        </Card>

        {asAdmin && (
          <Card>
            <h3 className="font-display text-base font-semibold text-[#1B1D1D]">
              Configurações RSM (admin)
            </h3>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Tier">
                <select
                  data-testid="editor-input-tier"
                  value={partner.tier || "certified"}
                  onChange={set("tier")}
                  className={inputCls}
                >
                  <option value="founding">Fundador</option>
                  <option value="certified">Certificado</option>
                  <option value="operator">Operador</option>
                </select>
              </Field>
              <Toggle
                label="Destaque"
                testid="editor-toggle-featured"
                checked={!!partner.featured}
                onChange={(v) => setPartner({ ...partner, featured: v })}
              />
              <Toggle
                label="Placeholder"
                testid="editor-toggle-placeholder"
                checked={!!partner.is_placeholder}
                onChange={(v) => setPartner({ ...partner, is_placeholder: v })}
              />
            </div>
          </Card>
        )}
      </div>

      <aside className="lg:col-span-4 space-y-4">
        <Card>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-display text-base font-semibold text-[#1B1D1D]">
                Disponibilidade
              </h3>
              <p className="mt-1 text-xs text-[#7D8188]">
                Sinalize ao mercado se você está aceitando novos projetos.
              </p>
            </div>
            <AvailabilityDot value={partner.availability || "accepting"} />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <AvailabilityOption
              testid="editor-availability-accepting"
              active={(partner.availability || "accepting") === "accepting"}
              onClick={() => setPartner({ ...partner, availability: "accepting" })}
              color="#3ED383"
              icon={CircleDot}
              label="Aceitando projetos"
              hint="Aparece em verde no diretório"
            />
            <AvailabilityOption
              testid="editor-availability-full"
              active={(partner.availability || "accepting") === "full"}
              onClick={() => setPartner({ ...partner, availability: "full" })}
              color="#7D8188"
              icon={CircleSlash}
              label="Lotado"
              hint="Sinaliza agenda cheia"
            />
          </div>
        </Card>

        <Card>
          <h3 className="font-display text-base font-semibold text-[#1B1D1D]">
            Logo
          </h3>
          <div className="mt-4 flex flex-col items-center gap-3">
            {partner.logo_url ? (
              <img
                src={
                  partner.logo_url.startsWith("/")
                    ? `${process.env.REACT_APP_BACKEND_URL}${partner.logo_url}`
                    : partner.logo_url
                }
                alt="Logo"
                data-testid="editor-logo-preview"
                className="h-24 w-24 rounded-2xl object-contain bg-[#F5FAFB] p-2 border border-[#1B1D1D]/10"
              />
            ) : (
              <div className="h-24 w-24 rounded-2xl bg-[#F5FAFB] border border-dashed border-[#1B1D1D]/20 grid place-items-center text-[#7D8188]">
                <ImagePlus size={22} />
              </div>
            )}
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              onChange={onUpload}
              data-testid="editor-logo-file"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              data-testid="editor-logo-upload"
              className="inline-flex items-center gap-2 rounded-full bg-[#1B1D1D] px-4 py-2 text-xs font-semibold text-white hover:bg-[#7E20FF] transition-colors disabled:opacity-60"
            >
              {uploading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <ImagePlus size={14} />
              )}
              {uploading ? "Enviando…" : "Enviar imagem (≤1 MB)"}
            </button>
            <p className="text-[11px] text-[#7D8188] text-center">
              PNG, JPG, WEBP ou SVG. Recomendado quadrado, mínimo 256px.
            </p>
          </div>
        </Card>

        <Card>
          <h3 className="font-display text-base font-semibold text-[#1B1D1D]">
            Slug
          </h3>
          <p className="mt-2 text-xs text-[#7D8188]">
            URL pública do seu perfil. Definida pela RSM e não pode ser alterada
            aqui.
          </p>
          <code className="mt-3 block rounded-lg bg-[#F5FAFB] border border-[#1B1D1D]/8 px-3 py-2 text-xs text-[#1B1D1D] font-mono break-all">
            /politics/parceiros/{partner.slug}
          </code>
          <a
            href={`/politics/parceiros/${partner.slug}`}
            target="_blank"
            rel="noreferrer"
            data-testid="editor-public-link"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#208BFF] hover:underline"
          >
            Ver perfil público <ExternalLink size={12} />
          </a>
        </Card>

        <button
          type="submit"
          disabled={saving}
          data-testid="editor-save"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#7E20FF] px-6 py-3.5 text-sm font-semibold text-white shadow-sm hover:bg-[#6818D6] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60"
        >
          {saving ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Save size={16} />
          )}
          {saving ? "Salvando…" : "Salvar alterações"}
        </button>
      </aside>
    </form>
  );
}

function Card({ children }) {
  return (
    <section className="rounded-2xl border border-[#1B1D1D]/8 bg-white p-6">
      {children}
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#7D8188]">
        {label}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className={`flex items-center justify-between rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
        checked
          ? "border-[#7E20FF] bg-[#7E20FF]/8 text-[#7E20FF]"
          : "border-[#1B1D1D]/12 text-[#51565F]"
      }`}
    >
      {label}
      <CheckCircle2 size={16} className={checked ? "" : "opacity-30"} />
    </button>
  );
}

const inputCls =
  "w-full rounded-xl border border-[#1B1D1D]/15 bg-white px-3.5 py-2.5 text-sm text-[#1B1D1D] placeholder:text-[#7D8188]/50 focus:border-[#7E20FF] focus:outline-none focus:ring-2 focus:ring-[#7E20FF]/20 transition-all";

function AvailabilityOption({ active, onClick, color, icon: Icon, label, hint, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      aria-pressed={active}
      className={`flex flex-col items-start gap-1.5 rounded-xl border p-3 text-left transition-all ${
        active
          ? "border-[#1B1D1D]/20 bg-white shadow-sm"
          : "border-[#1B1D1D]/8 bg-[#F5FAFB] opacity-70 hover:opacity-100"
      }`}
      style={active ? { borderColor: `${color}55`, background: `${color}10` } : undefined}
    >
      <Icon size={16} style={{ color }} />
      <span className="text-[12px] font-semibold leading-tight text-[#1B1D1D]">
        {label}
      </span>
      <span className="text-[10px] leading-snug text-[#7D8188]">{hint}</span>
    </button>
  );
}

function AvailabilityDot({ value }) {
  const accepting = value === "accepting";
  const color = accepting ? "#3ED383" : "#7D8188";
  const text = accepting ? "Aceitando" : "Lotado";
  return (
    <span
      data-testid="editor-availability-current"
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold tracking-[0.16em] uppercase"
      style={{ color, backgroundColor: `${color}15` }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
      {text}
    </span>
  );
}
